import {
  Box,
  Button,
  CardActions,
  CardContent,
  Container,
  Drawer,
  FormControl,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import 'dayjs/locale/ko';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { apiService } from '../../api/axiosClient';
import { FileUploaderSingle } from '../../components/upload';
import DropzoneWrapper from '../../styles/react-dropzone';

const DialogActivity = (props) => {
    const { activityID, show, setShow, setReload } = props;
    const [files, setFiles] = useState([]);
    const [activity, setActivity] = useState(null);
    const defaultValues = {
      author: activity?.author || '',
      activity_type: activity?.activity_type || '',
      activity_title: activity?.activity_title || '',
      company_meeting_participants: activity?.company_meeting_participants || '',
      meeting_participants: activity?.meeting_participants || '',
      content: activity?.content || '',
      result: activity?.result || '',
      activity_date: activity?.activity_date || '',
    };
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset
    } = useForm();

    useEffect(() => {
        if(show) {
            activityID ? getActivity(activityID) : setActivity(null);
        } else {
            reset(defaultValues);
            setFiles([]);
        }
    }, [show]);

    useEffect(() => {
        reset(defaultValues);
    }, [activity]);

    useEffect(() => {
        setFiles(activity?.file ? [activity?.file] : []);
    }, [activity?.file]);

    const getActivity = (id) => {
        const fetchData = async () => {
          try {
            const response = await apiService.get(`/activity/${id}`);
            const payload = response.data;
            setActivity(payload.data);
          } catch (e) {
            console.error('Error fetching activity:', e);
          }
        }
    
        fetchData();
    }

    const onSubmit = async (data, e) => {
        e.preventDefault();

        const [file] = files;
        const formData = new FormData()

        formData.append('author', data.author);
        formData.append('activity_type', data.activity_type);
        formData.append('activity_title', data.activity_title);
        formData.append('company_meeting_participants', data.company_meeting_participants);
        formData.append('meeting_participants', data.meeting_participants);
        formData.append('content', data.content);
        formData.append('result', data.result);
        formData.append('activity_date', data.activity_date);
        if(files.length > 0) {
          formData.append('file', file);
        }

        try {
            const response = await apiService.postFormData('/activity', formData);
            toast.success('등록 되었습니다.');
            setReload(reload => reload + 1);
        } catch (error) {
            toast.error('등록이 실패했습니다.');
            console.error('Error creating activity:', error);
        } finally {
            setShow(false);
        }
    }

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setShow(open);
    };

    return (
        <Drawer anchor='right' open={show} onClose={toggleDrawer(false)}>
            <Container sx={{width: '800px', pt: 8, px: '2rem !important'}}>
                <form onSubmit={handleSubmit(onSubmit)} id="activityform">
                    <CardContent sx={{ pt: 8 }}>
                        <Box sx={{ mb: 9, textAlign: 'center' }}>
                            <Typography variant='h5' sx={{ mb: 3, lineHeight: '2rem' }}>
                                Activity 등록
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                {/* 작성자 */}
                                <FormControl fullWidth margin="dense">
                                  <Controller
                                    name="author"
                                    control={control}
                                    rules={{ required: '필수 입력 항목입니다' }}
                                    render={({ field: { value, onChange } }) => (
                                      <TextField
                                        label="작성자"
                                        placeholder="작성자를 입력해주세요"
                                        value={value || ''}
                                        onChange={onChange}
                                        fullWidth
                                        error={Boolean(errors.author)}
                                        helpertext={errors.author ? errors.author.message : ''}
                                      />
                                    )}
                                  />
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              {/* 활동유형 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="activity_type"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="활동유형"
                                      placeholder="활동유형을 입력해주세요"
                                      value={value || ''}
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.activity_type)}
                                      helpertext={errors.activity_type ? errors.activity_type.message : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              {/* 활동제목 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="activity_title"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="활동제목"
                                      placeholder="활동제목을 입력해주세요"
                                      value={value || ''}
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.activity_title)}
                                      helpertext={errors.activity_title ? errors.activity_title.message : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              {/* 업체회의 참석자 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="company_meeting_participants"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="업체회의 참석자"
                                      placeholder="업체회의 참석자를 입력해주세요"
                                      value={value || ''}
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.company_meeting_participants)}
                                      helpertext={
                                        errors.company_meeting_participants ? errors.company_meeting_participants.message : ''
                                      }
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              {/* 회의 참석자 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="meeting_participants"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="회의 참석자"
                                      placeholder="회의 참석자를 입력해주세요"
                                      value={value || ''}
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.meeting_participants)}
                                      helpertext={errors.meeting_participants ? errors.meeting_participants.message : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              {/* 내용 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="content"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="내용"
                                      placeholder="내용을 입력해주세요"
                                      value={value || ''}
                                      rows={3}
                                      multiline
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.content)}
                                      helpertext={errors.content ? errors.content.message : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                              {/* 결과 */}
                              <FormControl fullWidth margin="dense">
                                <Controller
                                  name="result"
                                  control={control}
                                  rules={{ required: '필수 입력 항목입니다' }}
                                  render={({ field: { value, onChange } }) => (
                                    <TextField
                                      label="결과"
                                      placeholder="결과를 입력해주세요"
                                      value={value || ''}
                                      rows={2}
                                      multiline
                                      onChange={onChange}
                                      fullWidth
                                      error={Boolean(errors.result)}
                                      helpertext={errors.result ? errors.result.message : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Grid>                        

                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                  <Controller
                                      name='activity_date'
                                      control={control}
                                      rules={{ required: true }}
                                      defaultValue={dayjs().format("YYYY-MM-DD")}
                                      render={({ field: { value, onChange } }) => (
                                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
                                              <DatePicker
                                                  label='활동일자'
                                                  value={value}
                                                  onChange={onChange}
                                                  inputFormat={dayjs(value).format("YYYY-MM-DD")}
                                                  renderInput={(params) => <TextField {...params} />}
                                              />
                                          </LocalizationProvider>
                                      )}
                                  />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <DropzoneWrapper>
                                    <FileUploaderSingle files={files} setFiles={setFiles} update={true} />
                                </DropzoneWrapper>
                            </Grid>

                        </Grid>
                    </CardContent>
                </form>

                <CardActions className='card-action'>
                    <Button type="submit" form="activityform" variant='contained'> 등록 </Button>
                    <Button variant='outlined' color='secondary' onClick={() => setShow(false)}>
                        취소
                    </Button>
                </CardActions>
            </Container>
        </Drawer>        
    )
}

export default DialogActivity